import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getImpressoesPorPais } from '../../../../api/reportApi';
import ImpressionsByCountryChart from '../../pages/Report/ImpressionsByCountryChart';
import TableContainer from '../../../../components/TableContainerReactTable';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import numeral from 'numeral';

interface CountryMapCardProps {
  campaignId: string;
}

export interface CountryMapCardRef {
  refresh: () => void;
}

const CountryMapCard = forwardRef<CountryMapCardRef, CountryMapCardProps>(
  ({ campaignId }, ref) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasLoadedInitially, setHasLoadedInitially] = useState(false);
    numeral.locale('pt-br');
    const numeralInteger = '0,0';

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const countryData = await getImpressoesPorPais(parseInt(campaignId));
        setData(countryData);
        setHasLoadedInitially(true);
      } catch (err) {
        console.error('Error fetching country data:', err);
        setError('Erro ao carregar dados por país');
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      // Remove auto-fetch on mount - data will only be loaded when user clicks button
    }, []);

    useImperativeHandle(ref, () => ({
      refresh: fetchData
    }));

    // Show initial state with generate button if data hasn't been loaded yet
    if (!hasLoadedInitially && !loading) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Impressões por País</h4>
          </CardHeader>
          <CardContent>
            <div className="text-center py-5">
              <div className="mb-4">
                <i className="ri-global-line text-muted" style={{ fontSize: '3rem' }}></i>
              </div>
              <h5 className="text-muted mb-3">Relatório de Impressões por País</h5>
              <p className="text-muted mb-4">
                Clique no botão abaixo para gerar o relatório de impressões por país
                com mapa interativo e tabela de dados detalhados.
                Isso ajuda a reduzir a carga no banco de dados.
              </p>
              <Button
                onClick={fetchData}
                disabled={loading}
                className="px-4"
              >
                <i className="ri-bar-chart-line mr-2"></i>
                Gerar Relatório
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (loading) {
      return <SkeletonCard rows={2} columns={2} />;
    }

    if (error) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Impressões por País</h4>
          </CardHeader>
          <CardContent>
            <div className="text-center text-destructive">
              <i className="ri-error-warning-line mr-2"></i>
              {error}
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Impressões por País</h4>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted">
              <i className="ri-information-line mr-2"></i>
              Nenhum dado de país disponível
            </div>
          </CardContent>
        </Card>
      );
    }

    // Column definitions for the table
    const columns = [
      {
        header: 'País',
        accessorKey: 'country',
        cell: (row: any) => (
          <div className="font-medium">{row.getValue() || 'Unknown'}</div>
        ),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Impressões',
        accessorKey: 'impression',
        cell: (row: any) => numeral(row.getValue() || 0).format(numeralInteger),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Visíveis',
        accessorKey: 'viewable',
        cell: (row: any) => numeral(row.getValue() || 0).format(numeralInteger),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Cliques',
        accessorKey: 'click',
        cell: (row: any) => numeral(row.getValue() || 0).format(numeralInteger),
        enableColumnFilter: false,
        enableSorting: true,
      },
    ];

    return (
      <Card className={styles.reportCard}>
        <CardHeader className={styles.cardHeader}>
          <h4 className={styles.cardTitle}>Impressões por País</h4>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              {data && <ImpressionsByCountryChart series={data} />}
            </div>
            <div>
              <TableContainer
                divClass="table-responsive"
                columns={columns}
                data={data}
                isGlobalFilter={false}
                customPageSize={10}
                initialSorting={[{ id: 'impression', desc: true }]}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
);

CountryMapCard.displayName = 'CountryMapCard';

export default CountryMapCard;
